import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/common/DataTable";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreVertical, Copy, UserPlus, Users as UsersIcon } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const meta = useMeta();
  const isAdmin = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", username: "", mobile: "", role: "field_executive",
    reporting_manager: "", employment_status: "Employee", commission_plan: "", active: true, notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/users"); setRows(data);
      const m = await api.get("/users/managers"); setManagers(m.data);
    } catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.full_name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    setBusy(true);
    try {
      const payload = { ...form, reporting_manager: form.reporting_manager || null, username: form.username || null };
      const { data } = await api.post("/users", payload);
      setCreated(data);
      setOpen(false);
      setForm({ full_name: "", email: "", username: "", mobile: "", role: "field_executive", reporting_manager: "", employment_status: "Employee", commission_plan: "", active: true, notes: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doAction = async (uid, path, label) => {
    try {
      const { data } = await api.post(`/users/${uid}/${path}`);
      if (data?.temporary_password) setCreated({ user: rows.find((r) => r.id === uid), temporary_password: data.temporary_password, username: rows.find((r) => r.id === uid)?.username, invitation_sent: data.invitation_sent });
      else toast.success(label + " done");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "full_name", label: "User", render: (r) => <div><div className="font-semibold text-slate-800">{r.full_name}</div><div className="text-xs text-slate-400">{r.email} · @{r.username}</div></div> },
    { key: "role_label", label: "Role", render: (r) => r.role_label },
    { key: "mobile", label: "Mobile", mobileHide: true, render: (r) => r.mobile || "—" },
    { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "Approved" : "Reversed"} label={r.active ? "Active" : "Inactive"} /> },
    { key: "must_reset_password", label: "Onboarding", mobileHide: true, render: (r) => r.must_reset_password ? <StatusBadge status="possible" label="Pending reset" /> : <StatusBadge status="none" label="Complete" /> },
    ...(isAdmin ? [{
      key: "actions", label: "", render: (r) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button className="grid place-items-center h-9 w-9 rounded-lg hover:bg-slate-100" data-testid={`user-actions-${r.id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => doAction(r.id, "resend-invite", "Invite resent")} data-testid={`resend-${r.id}`}>Resend invitation</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doAction(r.id, "reset-password", "Password reset")}>Reset password</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doAction(r.id, "force-reset", "Forced reset")}>Force password reset</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doAction(r.id, "revoke-sessions", "Sessions revoked")}>Revoke sessions</DropdownMenuItem>
            {r.active
              ? <DropdownMenuItem className="text-rose-600" onClick={() => doAction(r.id, "deactivate", "Deactivated")} data-testid={`deactivate-${r.id}`}>Deactivate</DropdownMenuItem>
              : <DropdownMenuItem onClick={() => doAction(r.id, "activate", "Activated")}>Activate</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }] : []),
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Users & Access" subtitle="Create accounts and manage roles. No public sign-up." testid="users-header"
        actions={isAdmin && <Button onClick={() => setOpen(true)} className="bg-teal hover:bg-teal-700 h-11" data-testid="create-user-btn"><Plus className="h-4 w-4" /> Create User</Button>} />

      <DataTable columns={columns} rows={rows} loading={loading} error={error} onRetry={load} testid="users-table"
        empty={{ title: "No users", description: "Create your first team member or partner.", icon: UsersIcon }} />

      {/* Create user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>Create user</DialogTitle><DialogDescription>A one-time temporary password is generated and an invitation email is sent.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full name</Label><Input className="mt-1.5" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-name-input" /></div>
              <div><Label>Username (optional)</Label><Input className="mt-1.5" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="auto-generated" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
              <div><Label>Mobile</Label><Input className="mt-1.5" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta.roles || []).map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Reporting manager</Label>
                <Select value={form.reporting_manager || "none"} onValueChange={(v) => setForm({ ...form, reporting_manager: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-manager-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Employment status</Label><Input className="mt-1.5" value={form.employment_status} onChange={(e) => setForm({ ...form, employment_status: e.target.value })} /></div>
              <div><Label>Commission / incentive plan</Label><Input className="mt-1.5" value={form.commission_plan} onChange={(e) => setForm({ ...form, commission_plan: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea className="mt-1.5" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3"><Label>Active</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active-switch" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={busy} className="bg-teal hover:bg-teal-700" data-testid="save-user-btn"><UserPlus className="h-4 w-4" /> Create & Invite</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password result */}
      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader><DialogTitle>Account ready</DialogTitle><DialogDescription>Share these one-time credentials securely. {created?.invitation_sent ? "An invitation email was also sent." : "Email could not be sent — share manually."}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-2">
              <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Username</span><b className="text-slate-800">{created?.username}</b></div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Temporary password</span>
                <span className="flex items-center gap-2"><b className="font-mono text-slate-800" data-testid="temp-password-value">{created?.temporary_password}</b>
                  <button onClick={() => { navigator.clipboard?.writeText(created?.temporary_password || ""); toast.success("Copied"); }} className="text-slate-400 hover:text-teal"><Copy className="h-4 w-4" /></button>
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-400">The user must reset this password on first login. It expires in 72 hours.</p>
          </div>
          <DialogFooter><Button onClick={() => setCreated(null)} className="bg-teal hover:bg-teal-700" data-testid="close-temp-password">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
